/*
 * Construye la configuracion de dependencias entre columnas (cascada):
 * un mapa de padre -> hijos con las opciones permitidas por cada valor
 * del padre. Lo consume DependencyValidationManager para filtrar los
 * dropdowns de la hoja.
 *
 * Hay dos fuentes:
 *   - domain.parent_id (BD)  -> buildDerivedDependencyConfiguration
 *   - el literal de abajo    -> buildHardcodedDependencyConfiguration
 *
 * El literal es deuda historica: mapea NOMBRES de actividad a listas de
 * dominio. Si un nombre en BD no coincide con la clave, la rama cae al
 * fallback de DependencyValidationManager y el dropdown deja de filtrar
 * sin avisar. Se conserva solo como red de seguridad mientras parent_id
 * termina de poblarse; ver buildDependencyConfiguration al final.
 */
import { normalizeKey } from '../../shared/verteoffice/exports/helpers/helpers'
import type { DomainNode } from './buildDomainNodes'

export interface DomainDataList {
  [key: string]: string[]
}

export interface DependencyConfig {
  [parentColumn: string]: {
    children: string[]
    options: Record<string, Record<string, string[]>>
  }
}

export const buildHardcodedDependencyConfiguration = (
  domainDataList: DomainDataList
): DependencyConfig => {
  const safeList = (list?: string[]) => list ?? []
  const config: DependencyConfig = {
    general_activity: {
      children: ['specific_activity'],
      options: {
        default: {
          specific_activity: [],
        },
        'ATENCION AL CLIENTE': {
          specific_activity: safeList(domainDataList.activityObjectiveAtentionList)
        },
        'COMUNICACION': {
          specific_activity: safeList(domainDataList.activityObjectiveCommunicationList)
        },
        'DATA & DESARROLLO': {
          specific_activity: safeList(domainDataList.activityObjectiveDataDevList)
        },
        'DATA Y DESARROLLO': {
          specific_activity: safeList(domainDataList.activityObjectiveDataDevList)
        },
        'ESTRUCTURA DE PROCESOS': {
          specific_activity:
            safeList(domainDataList.activityObjectiveStructureProcessList)
        },
        'ESTRUCTURACION DE PROCESOS': {
          specific_activity:
            safeList(domainDataList.activityObjectiveStructureProcessList)
        },
        'GESTION DE PROCESOS': {
          specific_activity: safeList(domainDataList.activityObjectiveMngProcessesList)
        },
        'PLANEACION ESTRATEGICA': {
          specific_activity: safeList(domainDataList.activityObjectivePlanStrategyList)
        },
        'REPORTING': {
          specific_activity: safeList(domainDataList.activityObjectiveReportingList)
        },
        'TALENTO HUMANO Y AUSENTISMO': {
          specific_activity:
            safeList(domainDataList.activityObjectiveHumanTalentAbsenteeismList)
        }
      }
    },
    specific_activity: {
      children: ['activity_objective'],
      options: {
        default: {
          activity_objective: []
        },
        'ATENCION A SOLICITUDES': {
          activity_objective: safeList(domainDataList.specificActivityAttentionSoliList)
        },
        'MESA DE TRABAJO': {
          activity_objective: safeList(domainDataList.specificActivityAttentionTableList)
        },
        'DISEÑO': {
          activity_objective: safeList(domainDataList.specificActivityDesignList)
        },
        'SOPORTE': {
          activity_objective: safeList(domainDataList.specificActivitySupportList)
        },
        'DESARROLLO': {
          activity_objective: safeList(domainDataList.specificActivityDevList)
        },
        'CAPACIDAD': {
          activity_objective: safeList(domainDataList.specificActivityCapacityList)
        },
        'MODELOS BI': {
          activity_objective: safeList(domainDataList.specificActivityModelBiList)
        },
        'TABLEROS BI': {
          activity_objective: safeList(domainDataList.specificActivityTableBiList)
        },
        'ESTANDARIZACION': {
          activity_objective: safeList(domainDataList.specificActivityStandardList)
        },
        'INSTRUCTIVOS': {
          activity_objective: safeList(domainDataList.specificActivityInstructiveList)
        },
        'MALLA DE OPERACION': {
          activity_objective: safeList(domainDataList.specificActivityOperationList)
        },
        'GESTION ADMINISTRATIVA TH': {
          activity_objective: safeList(domainDataList.specificActivityAdminthList)
        },
        'GESTION CAPACITACION Y DESARROLLO': {
          activity_objective: safeList(domainDataList.specificActivityCapDevList)
        },
        'GESTION CONTABLE Y FINANCIERA': {
          activity_objective: safeList(domainDataList.specificActivityContFinList)
        },
        'GESTION ADMINISTRATIVA': {
          activity_objective: safeList(domainDataList.specificActivityMngAdminList)
        },
        'DESARROLLO DE NEGOCIOS E INNOVACION': {
          activity_objective: safeList(domainDataList.specificActivityDevBusinessInnovationList)
        },
        'GESTION CULTURA Y BIENESTAR': {
          activity_objective: safeList(domainDataList.specificActivityCultureWellbeingList)
        },
        'GESTION DE CAMPAÑAS Y CONVOCATORIAS': {
          activity_objective: safeList(domainDataList.specificActivityCampaignCallingList)
        },
        'GESTION DE CONTRATOS': {
          activity_objective: safeList(domainDataList.specificActivityMngContractList)
        },
        'GESTION DE DESVINCULACION': {
          activity_objective: safeList(domainDataList.specificActivityMngDisengagementList)
        },
        'GESTION ECOEFICIENCIA': {
          activity_objective: safeList(domainDataList.specificActivityMngEcoeficienciaList)
        },
        'GESTION DE PAGOS': {
          activity_objective: safeList(domainDataList.specificActivityMngPaymentsList)
        },
        'GESTION DE REPORTING': {
          activity_objective: safeList(domainDataList.specificActivityMngReportingList)
        },
        'GESTION DE SELECCION VINCULACION Y CONTRATACION': {
          activity_objective: safeList(domainDataList.specificActivityMngSelEngContrtList)
        },
        'GESTION DE SOSTENIBILIDAD': {
          activity_objective: safeList(domainDataList.specificActivityMngSustainabilityList)
        },
        'GESTION DESEMPEÑO': {
          activity_objective: safeList(domainDataList.specificActivityPerformanceList)
        },
        'GESTION DOCUMENTAL TH': {
          activity_objective: safeList(domainDataList.specificActivityMngDocumentThList)
        },
        'GESTION FUTURO V': {
          activity_objective: safeList(domainDataList.specificActivityMngFutureVList)
        },
        'GESTION INDUCCION - REINDUCCION': {
          activity_objective: safeList(domainDataList.specificActivityMngInductionReductionList)
        },
        'GESTION MERCADOS Y TARIFAS': {
          activity_objective: safeList(domainDataList.specificActivityMngMarketTariffsList)
        },
        'GESTION PLANEACION': {
          activity_objective: safeList(domainDataList.specificActivityMngPlanningList)
        },
        'GESTION SOLUCIONES TECNICAS': {
          activity_objective: safeList(domainDataList.specificActivityMngSolutionTechList)
        },
        'GESTIONES INTERNAS': {
          activity_objective: safeList(domainDataList.specificActivityInternalList)
        },
        'SG- SST': {
          activity_objective: safeList(domainDataList.specificActivitySgSstList)
        },
        'SISTEMA DE GESTION INTEGRAL': {
          activity_objective: safeList(domainDataList.specificActivitySystemMngIntegralList)
        },
        'VERTEBRA HELP (CLIENTE INTERNO)': {
          activity_objective: safeList(domainDataList.specificActivityVertebraHelpInternalList)
        },
        'ALINEACION ESTRATEGICA': {
          activity_objective: safeList(domainDataList.specificActivityAlignStrategyList)
        },
        'COORDINACION AREAS': {
          activity_objective: safeList(domainDataList.specificActivityCoordinationAreasList)
        },
        'DIRECCION EQUIPO': {
          activity_objective: safeList(domainDataList.specificActivityDirectionTeamList)
        },
        'ESTRATEGIA EMPRESA': {
          activity_objective: safeList(domainDataList.specificActivityStrategyCompanyList)
        },
        'REPORTES ECOEFICIENCIA': {
          activity_objective: safeList(domainDataList.specificActivityReportsEcoeficienciaList)
        },
        'REPORTES INTERNOS': {
          activity_objective: safeList(domainDataList.specificActivityReportsInternalList)
        },
        'REPORTES MERCADOS Y TARIFAS': {
          activity_objective: safeList(domainDataList.specificActivityReportsMarketTarrifsList)
        },
        'REPORTES PAGOS': {
          activity_objective: safeList(domainDataList.specificActivityReportsPaymentsList)
        },
        'REPORTES SOLUCIONES TECNICAS': {
          activity_objective: safeList(domainDataList.specificActivityReportsSolutionsTechList)
        },
        'AUSENCIA LABORAL': {
          activity_objective: safeList(domainDataList.specificActivityAbsentWorkList)
        },
        'CULTURA VERTEBRA': {
          activity_objective: safeList(domainDataList.specificActivityCulturalVertebraList)
        },
        'DESVINCULACION': {
          activity_objective: safeList(domainDataList.specificActivityDisengagementList)
        },
        'INDUCCION - RE INDUCCION': {
          activity_objective: safeList(domainDataList.specificActivityInductionReinductionList)
        },
        'SELECCION': {
          activity_objective: safeList(domainDataList.specificActivitySelectionList)
        },
        'SISTEMA DE GESTION': {
          activity_objective: safeList(domainDataList.specificActivitySystemMngList)
        }
      }
    }
  }

  return config
}

// ─────────────────────────────────────────────────────────────────────
// Derivacion desde domain.parent_id
// ─────────────────────────────────────────────────────────────────────

const isObjectiveType = (type: string) => type.startsWith('ACTIVITY_OBJECTIVE')
const isSpecificType = (type: string) => type.startsWith('SPECIFIC_ACTIVITY')

const uniqueNames = (nodes: DomainNode[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const node of nodes) {
    const name = node.name.trim()
    if (!name) continue
    const key = normalizeKey(name)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(name)
  }
  return out
}

/* Agrupa los nodos por parent_id y arma el mismo shape que el literal,
   usando el NOMBRE del padre como clave (DependencyValidationManager
   matchea las claves con normalizeKey, asi que sirve cualquier grafia). */
export const buildDerivedDependencyConfiguration = (
  nodes: DomainNode[]
): DependencyConfig => {
  const childrenByParentId = new Map<string, DomainNode[]>()
  for (const node of nodes) {
    if (!node.parent_id) continue
    const siblings = childrenByParentId.get(node.parent_id)
    if (siblings) siblings.push(node)
    else childrenByParentId.set(node.parent_id, [node])
  }

  const generalOptions: Record<string, Record<string, string[]>> = {}
  const objectiveOptions: Record<string, Record<string, string[]>> = {}

  for (const node of nodes) {
    const children = childrenByParentId.get(node.id)
    if (!children?.length) continue

    if (node.type === 'GENERAL_ACTIVITY') {
      const objectives = uniqueNames(children.filter(child => isObjectiveType(child.type)))
      if (objectives.length) {
        generalOptions[node.name] = { specific_activity: objectives }
      }
      continue
    }

    if (isObjectiveType(node.type)) {
      const specifics = uniqueNames(children.filter(child => isSpecificType(child.type)))
      if (specifics.length) {
        objectiveOptions[node.name] = { activity_objective: specifics }
      }
    }
  }

  return {
    general_activity: {
      children: ['specific_activity'],
      options: { default: { specific_activity: [] }, ...generalOptions },
    },
    specific_activity: {
      children: ['activity_objective'],
      options: { default: { activity_objective: [] }, ...objectiveOptions },
    },
  }
}

/* Fusiona ambas fuentes RAMA POR RAMA: si la BD trae hijos para un padre,
   gana la BD; si no, se conserva la entrada del literal.
   La entrada hardcodeada se descarta cuando existe una derivada que
   normaliza igual — dejar las dos haria ambiguo el `find` por clave
   normalizada de DependencyValidationManager.updateDependentValidations. */
const mergeOptionsByBranch = (
  hardcoded: Record<string, Record<string, string[]>>,
  derived: Record<string, Record<string, string[]>>,
  childColumn: string
): Record<string, Record<string, string[]>> => {
  const derivedKeyByNormalized = new Map<string, string>()
  for (const key of Object.keys(derived)) {
    if (key === 'default') continue
    derivedKeyByNormalized.set(normalizeKey(key), key)
  }

  const hasDerivedBranch = (key: string): boolean => {
    const derivedKey = derivedKeyByNormalized.get(normalizeKey(key))
    if (!derivedKey) return false
    return (derived[derivedKey]?.[childColumn] ?? []).length > 0
  }

  const merged: Record<string, Record<string, string[]>> = {}
  const takenNormalizedKeys = new Set<string>()

  for (const [key, value] of Object.entries(hardcoded)) {
    if (key === 'default') {
      merged[key] = value
      continue
    }
    if (hasDerivedBranch(key)) continue

    // El literal trae alias que normalizan igual ('DATA & DESARROLLO' y
    // 'DATA Y DESARROLLO', porque normalizeKey convierte & en Y). Dejar
    // ambos haria que el find por clave normalizada dependiera del orden
    // de las propiedades. Gana el primero, que es el que ya ganaba.
    const normalized = normalizeKey(key)
    if (takenNormalizedKeys.has(normalized)) continue
    takenNormalizedKeys.add(normalized)

    merged[key] = value
  }

  for (const [key, value] of Object.entries(derived)) {
    if (key === 'default') continue
    if ((value?.[childColumn] ?? []).length === 0) continue
    merged[key] = value
  }

  return merged
}

/* Interruptor de la fuente de la jerarquia de actividades.
 *
 *   true (defecto) -> la cascada sale UNICAMENTE de domain.parent_id. El
 *                     literal de arriba queda inactivo. Una rama ausente en
 *                     BD se queda sin opciones, y entonces
 *                     DependencyValidationManager cae a su union-de-todo: el
 *                     dropdown muestra el catalogo completo. Ese sintoma es
 *                     la senal de que a esa rama le falta parent_id.
 *   false          -> las ramas que la BD no cubra usan el literal como
 *                     respaldo (comportamiento de transicion, ya superado).
 *
 * Se dejo activado por defecto tras verificar en frontend que el arbol de BD
 * reproduce la cascada completa. El literal se conserva un tiempo como red de
 * seguridad reversible; se borrara en el PR de limpieza junto con
 * activityTypeMapping y las 59 entradas sobrantes de DOMAIN_CONFIG.
 */
export const ACTIVITY_HIERARCHY_DB_ONLY = true

/* En modo solo-BD, avisa una vez por cada rama que el literal conoce y la
   BD no. Es el diagnostico que permite distinguir "el dropdown no filtra"
   de "a esta rama le falta parent_id". */
const warnBranchesMissingInDb = (
  hardcoded: DependencyConfig,
  derived: DependencyConfig
): void => {
  const pairs: Array<[string, string]> = [
    ['general_activity', 'specific_activity'],
    ['specific_activity', 'activity_objective'],
  ]

  for (const [parentColumn, childColumn] of pairs) {
    const derivedKeys = new Set(
      Object.keys(derived[parentColumn]?.options ?? {})
        .filter(key => key !== 'default')
        .map(normalizeKey)
    )
    const missing = Object.entries(hardcoded[parentColumn]?.options ?? {})
      .filter(([key, value]) =>
        key !== 'default' &&
        (value?.[childColumn] ?? []).length > 0 &&
        !derivedKeys.has(normalizeKey(key))
      )
      .map(([key]) => key)

    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `[registroHoras] jerarquia solo-BD: ${parentColumn} sin cobertura en domain.parent_id ->`,
        missing
      )
    }
  }
}

interface BuildDependencyConfigurationOptions {
  /** Por defecto toma ACTIVITY_HIERARCHY_DB_ONLY. Explicito en los tests. */
  dbOnly?: boolean
  /** Apaga warnBranchesMissingInDb. Para builds sobre un catalogo YA filtrado
   *  por area (areaScopedDependencyConfig): ahi una rama ausente es el filtro
   *  funcionando, no un parent_id faltante en BD, y el warn es falso positivo. */
  suppressMissingBranchWarnings?: boolean
}

// La cascada se consulta por fila al validar y al editar, asi que se
// memoiza por identidad de las entradas, una cache por modo.
const NO_NODES = Symbol('no-nodes')
type ConfigCache = WeakMap<DomainDataList, Map<DomainNode[] | symbol, DependencyConfig>>
const configCacheByMode: Record<'dbOnly' | 'withFallback', ConfigCache> = {
  dbOnly: new WeakMap(),
  withFallback: new WeakMap(),
}

export const buildDependencyConfiguration = (
  domainDataList: DomainDataList,
  nodes?: DomainNode[],
  options?: BuildDependencyConfigurationOptions
): DependencyConfig => {
  const dbOnly = options?.dbOnly ?? ACTIVITY_HIERARCHY_DB_ONLY
  const configCache = configCacheByMode[dbOnly ? 'dbOnly' : 'withFallback']

  const cacheKey: DomainNode[] | symbol = nodes?.length ? nodes : NO_NODES
  const byNodes = configCache.get(domainDataList)
  const cached = byNodes?.get(cacheKey)
  if (cached) return cached

  const hardcoded = buildHardcodedDependencyConfiguration(domainDataList)
  const derived = buildDerivedDependencyConfiguration(nodes ?? [])

  let config: DependencyConfig
  if (dbOnly) {
    if (!options?.suppressMissingBranchWarnings) {
      warnBranchesMissingInDb(hardcoded, derived)
    }
    config = derived
  } else if (!nodes?.length) {
    config = hardcoded
  } else {
    config = {
      general_activity: {
        children: ['specific_activity'],
        options: mergeOptionsByBranch(
          hardcoded.general_activity?.options ?? {},
          derived.general_activity?.options ?? {},
          'specific_activity'
        ),
      },
      specific_activity: {
        children: ['activity_objective'],
        options: mergeOptionsByBranch(
          hardcoded.specific_activity?.options ?? {},
          derived.specific_activity?.options ?? {},
          'activity_objective'
        ),
      },
    }
  }

  if (byNodes) byNodes.set(cacheKey, config)
  else configCache.set(domainDataList, new Map([[cacheKey, config]]))

  return config
}
